import { ref, shallowRef, onUnmounted } from 'vue';
import type { Socket } from 'socket.io-client';
import { useAuth } from '@/composables/auth';
import { AppAuthTokenKey, SelectedStoreIdKey } from '@/consts';
import { ReviewSocketEvents } from '@nailsupply/review-swagger/reviews';
import { BillingSocketEvents } from '@nailsupply/review-swagger/billings';
import type { ReviewSocketData } from './types';

// Shared state — all callers of useReviewSocket() share the same socket instance.
// shallowRef (không phải ref): Socket là instance ngoài, không cần deep-reactive; đồng thời
// tránh UnwrapRef<Socket> làm rớt field private của Socket (bản mới) → lệch với annotation.
const socket = shallowRef<Socket | null>(null);
const isConnected = ref(false);
const error = ref<string | null>(null);
// Dedupe các lần connect() gọi song song trong lúc dynamic import socket.io-client chưa resolve
// (io() set socket.value không còn đồng bộ nữa) → tránh tạo 2 socket.
let connectingPromise: Promise<Socket | null> | null = null;

export const useReviewSocket = () => {
  const { getAuthCookieByKey } = useAuth();
  const token = getAuthCookieByKey(AppAuthTokenKey);

  const connect = async (): Promise<Socket | null> => {
    if (socket.value?.connected) {
      return socket.value;
    }
    // Đang có 1 lần connect chạy dở (import chưa xong) → tái dùng thay vì tạo socket thứ 2.
    if (connectingPromise) {
      return connectingPromise;
    }

    connectingPromise = (async () => {
      try {
        // Lazy-load socket.io-client: chỉ tải khi thật sự cần connect, để nó rời entry chunk
        // (App.vue / main.vue import useReviewSocket eager nhưng phần lớn phiên không mở review).
        const { io } = await import('socket.io-client');
        const storeId = localStorage.getItem(SelectedStoreIdKey) || undefined;

        socket.value = io(import.meta.env.VITE_SOCKET_URL, {
          auth: { token },
          query: { storeId },
          transports: ['websocket'],
          reconnection: true,
          reconnectionDelay: 1000,
          reconnectionAttempts: 5,
        });


        socket.value.on('connect', () => {
          isConnected.value = true;
          error.value = null;
          console.log('Socket connected to reviews namespace');
        });

        socket.value.on('disconnect', () => {
          isConnected.value = false;
          console.log('Socket disconnected from reviews namespace');
        });

        socket.value.on('connect_error', (err) => {
          error.value = err.message;
          console.error('Socket connection error:', err);
        });

        socket.value.on('error', (err) => {
          error.value = err.message;
          console.error('Socket error:', err);
        });

        return socket.value;
      } catch (err) {
        error.value = err instanceof Error ? err.message : 'Unknown error';
        console.error('Failed to create socket:', err);
        return null;
      }
    })();

    // IIFE trên tự bắt mọi lỗi (luôn return null, không reject) nên await không throw;
    // connect xong thì clear guard để lần sau (khi socket rớt) tạo lại được.
    const newSocket = await connectingPromise;
    connectingPromise = null;
    return newSocket;
  };

  const disconnect = () => {
    if (socket.value) {
      socket.value.disconnect();
      socket.value = null;
      isConnected.value = false;
    }
  };


  const startReviewSession = async (data: ReviewSocketData) => {
    if (!socket.value?.connected) {
      await connect();
    }

    socket.value?.emit(ReviewSocketEvents.enum.request_review, data, (response: any) => {
      if (response?.success) {
        console.log('Review session started:', response);
      } else {
        console.error('Failed to start review session:', response);
      }
    });
  };


  const onReviewSessionStart = async (callback: (data: any) => void) => {
    if (!socket.value?.connected) {
      await connect();
    }
    socket.value?.on(ReviewSocketEvents.enum.show_review_form, callback);
  };

  const onBillingSessionStart = async (callback: (data: any) => void) => {
    if (!socket.value?.connected) {
      await connect();
    }
    socket.value?.on(BillingSocketEvents.enum.billing_created, callback);
  };

  const submitReview = (reviewData: any) => {
    if (!socket.value?.connected) {
      console.error('Socket not connected');
      return;
    }

    socket.value.emit(ReviewSocketEvents.enum.review_submitted, reviewData, (response: any) => {
      if (response?.success) {
        console.log(' Review submitted:', response);
      } else {
        console.error(' Failed to submit review:', response);
      }
    });
  };

  const onReviewSubmitted = async (callback: (data: any) => void) => {
    if (!socket.value?.connected) {
      await connect();
    }
    socket.value?.on(ReviewSocketEvents.enum.review_submitted, callback);
  };

  const onDeviceCommand = async (callback: (data: { command: string; payload?: Record<string, unknown>; timestamp: string }) => void) => {
    if (!socket.value?.connected) {
      await connect();
    }
    socket.value?.on('device:command', callback);
  };

  const removeAllListeners = () => {
    socket.value?.off('customer:review-started');
    socket.value?.off('customer:review-submitted');
  };

  onUnmounted(() => {
    removeAllListeners();
    disconnect();
  });

  return {
    socket,
    isConnected,
    error,
    connect,
    disconnect,
    startReviewSession,
    onReviewSessionStart,
    submitReview,
    onReviewSubmitted,
    removeAllListeners,
    onBillingSessionStart,
    onDeviceCommand,
  };
};
